import { ChatMessage } from 'models/ai-chat/chat.types';
import React from 'react';
import ChatMessageComponent from './chat-message';
import { useChat } from 'store/useChatStore';
import { useAppDispatch } from 'redux/hooks';
import { updateMessageData } from 'features/chat-slice';

interface MessageListProps {
  messages: ChatMessage[];
}

const MessageList: React.FC<MessageListProps> = ({ messages }) => {
  const {feedbackMessageRequest, sendChatMessage} = useChat()
  const dispatch = useAppDispatch();
  
 
  const handleFeedback = (messageId: string, type: 'thumbs_up' | 'thumbs_down', parentMessageId?: string) => {
    // Use parentMessageId for API (backend expects parent message ID)
    const apiMessageId = parentMessageId || messageId;
    
    const feedbackRequest = {
      message_id: apiMessageId,
      feedback_type: type,
      feedback_value: type === 'thumbs_up' ? 'true' : 'false',
      state_message_id: messageId, // The actual message being rendered (for streaming direct match)
      parent_message_id: parentMessageId // The parent ID (for loaded messages & fallback)
    };
    feedbackMessageRequest(feedbackRequest)
  };

  const handleFilterUpdateComplete = (messageId: string, updatedData: any) => {
    dispatch(updateMessageData({
      messageId,
      newData: updatedData
    }));
  };

  const handleMessageEdit = (messageId: string, newContent: string) => {
    // Call the API with edit prefix format
    const editMessage = `[EDIT:${messageId}] ${newContent}`;
    console.log('Sending edit message:', editMessage);
    sendChatMessage(editMessage);
  };

  const handleRegenerateInsights = (messageId: string, originalPrompt: string) => {
    console.log('🔄 handleRegenerateInsights called:', { messageId, originalPrompt, messagesCount: messages.length });
    
    // Find the message (could be top-level or submessage)
    let targetMessage: ChatMessage | null = null;
    let parentUserMessage: ChatMessage | null = null;
    
    // First, check top-level messages
    const messageIndex = messages.findIndex(msg => msg.message_id === messageId);
    console.log('🔍 Message index in top-level:', messageIndex);
    
    if (messageIndex !== -1) {
      targetMessage = messages[messageIndex];
      if (messageIndex > 0) {
        parentUserMessage = messages[messageIndex - 1];
      }
    } else {
      // If not found, search in submessages
      console.log('🔍 Searching in submessages...');
      for (const msg of messages) {
        if (msg.submessages && msg.submessages.length > 0) {
          const submessageIndex = msg.submessages.findIndex(sub => sub.message_id === messageId);
          if (submessageIndex !== -1) {
            targetMessage = msg.submessages[submessageIndex];
            // The parent user message is the parent of the parent message (the one containing submessages)
            const parentIndex = messages.findIndex(m => m.message_id === msg.message_id);
            if (parentIndex > 0) {
              parentUserMessage = messages[parentIndex - 1];
            }
            break;
          }
        }
      }
    }
    
    console.log('🔍 Found:', { targetMessage: !!targetMessage, parentUserMessage: !!parentUserMessage, parentRole: parentUserMessage?.role });
    
    if (targetMessage && parentUserMessage && parentUserMessage.role === 'user') {
      const regenerateMessage = `[REGENERATE:${messageId}] ${parentUserMessage.content}`;
      console.log('✅ Sending regenerate message:', regenerateMessage);
      sendChatMessage(regenerateMessage);
    } else if (parentUserMessage && parentUserMessage.role !== 'user') {
      console.warn('⚠️ Parent message is not a user message:', parentUserMessage);
    } else {
      console.warn('❌ Could not find parent user message for regenerate:', { messageId, hasTarget: !!targetMessage, hasParent: !!parentUserMessage });
    }
  };

  const renderMessageWithSubmessages = (message: ChatMessage) => {
    if (message.submessages && message.submessages.length > 0 && message.role === 'assistant') {
      
      return (
        <React.Fragment key={message.message_id}>
          {message.submessages.map((submessage, index) => {
            const isLastSubmessage = index === message.submessages!.length - 1;
            // Inherit parent's feedback and parent_message_id for submessages
            const submessageWithParentFeedback = {
              ...submessage,
              feedback_type: submessage.feedback_type || message.feedback_type,
              feedback_value: submessage.feedback_value || message.feedback_value,
              // Pass parent_message_id so chat-message.tsx can use it for feedback
              parent_message_id: message.parent_message_id || message.message_id,
            };
            return (
              <ChatMessageComponent
                key={submessage.message_id}
                message={submessageWithParentFeedback}
                onFeedback={handleFeedback}
                onMessageEdit={handleMessageEdit}
                onRegenerateInsights={handleRegenerateInsights}
                onVisualizationChange={
                 (messageId: string, newType: string) => {
                    // Handle chart type change
                    // console.log(`Message ${messageId} changed to ${newType}`);
                  }
                }
                onFilterUpdateComplete={handleFilterUpdateComplete}
                showActionButtons={isLastSubmessage}
              />
            );
          })}
        </React.Fragment>
      );
    }
    
    return (
      <ChatMessageComponent
        key={message.message_id}
        message={message}
        onFeedback={handleFeedback}
        onMessageEdit={handleMessageEdit}
        onRegenerateInsights={handleRegenerateInsights}
        onVisualizationChange={
         (messageId: string, newType: string) => {
            // Handle chart type change
            // console.log(`Message ${messageId} changed to ${newType}`);
          }
        }
        onFilterUpdateComplete={handleFilterUpdateComplete}
        showActionButtons={true}
      />
    );
  };

  return (
    <div className="space-y-6">
      {messages.map((message) => renderMessageWithSubmessages(message))}
    </div>
  );
};

export default MessageList;